import React, { useState } from 'react';
import { styled, TokenTextAppearance, HandleCheckboxEvent, TextTag, Text, InputAppearanceValue } from '@volkswagen-onehub/components-core';
import { getCommonProps } from '@volkswagen-onehub/components-core/dist/helpers/common-props';
import { Check } from '../../components/Check';
import { ModelOptionDetails } from './ModelOptionDetails';
import { getContrastColor } from '../../utils/get-contrast-color';

type Props = {
  appearance?: InputAppearanceValue;
  textAppearance?: TokenTextAppearance;
  message?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  label?: React.ReactNode;
  name?: string;
  onBlur?: HandleCheckboxEvent;
  onChange?: HandleCheckboxEvent;
  onClick?: HandleCheckboxEvent;
  onFocus?: HandleCheckboxEvent;
  onLabelMouseDown?: React.MouseEventHandler<HTMLLabelElement>;
  required?: boolean;
  tabIndex?: -1 | 0;
  color?: string;
  value: string;
  innerRef?: React.Ref<HTMLInputElement>;
  // name: string;
  image: string;
  alt?: string;
}

const StyledLabel = styled.label`
  position: relative;
  color: ${(props) => props.theme.colors.content.active};
  cursor: pointer;
`;

const StyledFocusWrapper = styled.span`
  position: relative;
  display: grid;
  justify-items: center;
  align-items: center;
  &:hover {
    &::before {
      content: '';
      position: absolute;
      top: -8px;
      width: calc(100% + 16px);
      height: calc(100% + 16px);
      background-color: rgba(194,202,207,0.2);
    }
  }
`;

const StyledCheckWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(8px, -8px);
  opacity: 0;
`;

const ImageWrapper = styled.div`
  width: 100%;
  position: absolute;
  bottom: -33%;
  img {
    width: 100%;
  }
`;

const StyledModel = styled.div`
  width: 100%;
  padding: 0 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 2px solid #DFE4E8;
  margin-bottom: 10%;
  padding: 0;
  padding-top: 67%;
  position: relative;
  background-color: ${({ color }) => color ? color : 'goldenrod'};
  border: 0;
`;

const TextContainer = styled.div`
  padding: 20px;
  position: absolute;
  top: 0;
  left: 0;
  h3, p {
    color: ${({ color }) => color ? getContrastColor(color) : "#000000"};
    padding: 0;
  }
  .count {
    padding-top: 8px;
    span {
      display: block;
      &.bold {
        font-weight: bold;
        display: inline;
      }
    }
  }
`;

const StyledInput = styled.input`
  position: absolute;
  opacity: 0;
  outline: 0;
  user-select: none;
  pointer-events: none;
  :checked {
    + ${StyledFocusWrapper} {
      color: ${(props) => props.theme.colors.content.active};
      ${StyledCheckWrapper} {
        opacity: 1;
      }
     ${StyledModel} {
            // border-color: #4CC7F4;
            border-color: red;
          }

    }
  }
  :focus {
    + ${StyledFocusWrapper} {
      outline: 2px solid rgb(0, 64, 197);
      outline-offset: 4px;
    }
  }
  :disabled {
    + ${StyledFocusWrapper} {
      color: ${(props) => props.theme.colors.content.secondary};
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const noop = () => undefined;

export const Model = (props: Props) => {
  const {
    checked,
    defaultChecked,
    label = "",
    name,
    value,
    required,
    tabIndex,
    onLabelMouseDown,
    onChange = noop,
    onBlur = noop,
    onFocus = noop,
    onClick = noop,
    innerRef,
    color,
    image,
    alt
  } = props;

  const [ isDisabled, setIsDisabled ] = useState(false);

  const handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    onChange(e, props);
  };

  const handleBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    onBlur(e, props);
  };

  const handleFocus = (e: React.SyntheticEvent<HTMLInputElement>) => {
    onFocus(e, props);
  };

  const handleClick = (e: React.SyntheticEvent<HTMLInputElement>) => {
    onClick(e, props);
  };

  const commonProps = getCommonProps(props);

  const disableOption = (bool: boolean) => {
    setIsDisabled(bool);
  };

  return (
    <StyledLabel onMouseDown={onLabelMouseDown} {...commonProps}>
      <StyledInput
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={isDisabled}
        name={name}
        onBlur={handleBlur}
        onChange={handleChange}
        onClick={handleClick}
        onFocus={handleFocus}
        type={'checkbox'}
        value={value}
        required={required}
        tabIndex={tabIndex}
        ref={innerRef}
      />
      <StyledFocusWrapper>
        <StyledModel color={color}>
          <TextContainer color={color}>
            <Text tag={TextTag.p} appearance={TokenTextAppearance.headline300} bold>{ label }</Text>
            <ModelOptionDetails
              label={label as string}
              color={color as string}
              modelKey={value as string}
              disableOption={disableOption}
            />
          </TextContainer>
          {
            image
            && <ImageWrapper>
              <img src={image} alt={alt} />
            </ImageWrapper>
          }
          <StyledCheckWrapper>
            <Check size="large" ariaHidden />
          </StyledCheckWrapper>
        </StyledModel>
      </StyledFocusWrapper>
    </StyledLabel>
  );
};
